import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { CheckCircleIcon, CloseCircleIcon, SearchIcon, WarningIcon } from "../../icons/glyphs";
import { AnchoredPopup, useAnchoredPopupAnchor } from "../AnchoredPopup";
import { IconButton } from "../IconButton";
import { NotificationBadge } from "../NotificationBadge";
import { Spinner } from "../Spinner";
import { Text, defaultTextVariants } from "../Text";
import { Tooltip, useTooltipController } from "../Tooltip";
import { TruncatedText } from "../TruncatedText";
import { controlFocusRing } from "../foundations/controlStyles";
import { interactiveSurfaceCss } from "../foundations/interactiveSurface";
import { FrameAnnotationGrid } from "./FrameAnnotationGrid";
import { annotationStudioText } from "./messages";
import type {
  AnnotationSetValidationIssue,
  AnnotationSetValidationState,
  AnnotationStudioLocale,
  AnnotationTarget,
} from "./types";

export interface AnnotationValidationIssueFrameLocation {
  readonly frameId: string;
  readonly path: string;
  readonly timestampLabel: string;
  readonly contextLabel: string;
  readonly annotatedTargetIds: ReadonlySet<string>;
}

export interface AnnotationValidationIssueLocation {
  readonly frames: readonly AnnotationValidationIssueFrameLocation[];
}

const PopupTitle = styled(Text)`
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`;

const ValidationSummary = styled.div<{
  $status: "idle" | "validating" | "ready" | "blocked" | "stale" | "error";
}>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 0.55rem;
  color: ${({ $status, theme }) =>
    $status === "blocked" || $status === "error"
      ? theme.color.text.warning
      : $status === "ready"
        ? theme.color.status.success
        : theme.color.text.default};

  & > svg,
  & > span:first-child {
    margin-top: 0.05rem;
    font-size: ${({ theme }) => theme.typography.size.title};
  }
`;

const ValidationSummaryText = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const ValidationSummaryButton = styled(ValidationSummary).attrs({
  as: "button",
  type: "button",
})`
  width: 100%;
  padding: 0.45rem 0.5rem;
  border: 1px solid;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  font: inherit;
  text-align: left;
  ${({ $status, theme }) => {
    const foreground =
      $status === "blocked" || $status === "error"
        ? theme.color.text.warning
        : $status === "ready"
          ? theme.color.status.success
          : theme.color.text.default;
    return interactiveSurfaceCss({
      rest: {
        background: theme.color.bg.surfaceSunken,
        border: theme.color.border.divider,
        foreground,
      },
      hover: {
        background: theme.color.bg.surfaceRaisedHigh,
        border: theme.color.border.default,
        foreground,
      },
      active: {
        background: theme.color.bg.surfaceOverlay,
        border: theme.color.border.default,
        foreground,
      },
      disabled: {
        background: theme.color.bg.control.disabledSunken,
        border: theme.color.border.disabled,
        foreground: theme.color.text.disabled,
      },
    });
  }}

  &:focus-visible {
    ${controlFocusRing("-2px")}
  }
`;

const ValidationDescription = styled(Text)`
  color: ${({ theme }) => theme.color.text.subtle};
`;

const ValidationIssueCount = styled(Text)`
  color: ${({ theme }) => theme.color.text.subtle};
`;

const ValidationLocation = styled(TruncatedText)`
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-variant-numeric: tabular-nums;
  font-weight: ${({ theme }) => theme.typography.weight.regular};
`;

const ValidationIssueList = styled.ul`
  display: grid;
  gap: 0.25rem;
  margin: 0.65rem 0 0;
  padding: 0;
  list-style: none;
`;

const ValidationIssueCard = styled.article`
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.border.divider};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
  color: ${({ theme }) => theme.color.text.default};
`;

const ValidationIssueHeader = styled.div`
  box-sizing: border-box;
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 0.45rem;
  padding: 0.45rem 0.5rem;

  & > svg {
    margin-top: 0.05rem;
    color: ${({ theme }) => theme.color.text.warning};
  }
`;

const ValidationIssueText = styled.span`
  display: grid;
  min-width: 0;
  gap: 0.15rem;
  font-size: ${({ theme }) => theme.typography.size.caption};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
`;

const ValidationRelatedFrameSection = styled.div`
  display: grid;
  gap: 0.3rem;
  padding: 0 0.5rem 0.5rem;
`;

const ValidationRelatedFrameLabel = styled.span`
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`;

const ValidationRelatedFrameList = styled.ul`
  display: grid;
  gap: 1px;
  overflow: hidden;
  margin: 0;
  padding: 1px;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.border.divider};
  list-style: none;
`;

const ValidationRelatedFrameButton = styled.button<{ $current: boolean }>`
  position: relative;
  box-sizing: border-box;
  display: grid;
  width: 100%;
  min-width: 0;
  min-height: 2.75rem;
  grid-template-columns: 22px minmax(0, 1fr);
  grid-template-rows: auto auto;
  align-items: center;
  gap: 0.05rem 0.45rem;
  padding: 0.32rem 0.45rem;
  border: 0;
  border-radius: 0;
  font: inherit;
  text-align: left;
  ${({ $current, theme }) =>
    interactiveSurfaceCss({
      rest: {
        background: $current ? theme.color.bg.selected : theme.color.bg.surfaceRaised,
        border: "transparent",
        foreground: theme.color.text.default,
      },
      hover: {
        background: $current ? theme.color.bg.selectedHover : theme.color.bg.surfaceRaisedHigh,
        border: "transparent",
        foreground: theme.color.text.default,
      },
      active: {
        background: $current ? theme.color.bg.selectedActive : theme.color.bg.surfaceOverlay,
        border: "transparent",
        foreground: theme.color.text.default,
      },
      disabled: {
        background: theme.color.bg.control.disabledSunken,
        border: "transparent",
        foreground: theme.color.text.disabled,
      },
    })}

  &:focus-visible {
    ${controlFocusRing("-2px")}
  }

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    background: ${({ theme }) => theme.color.border.selected};
    content: "";
    opacity: ${({ $current }) => ($current ? 1 : 0)};
    pointer-events: none;
  }

  & > [data-validation-related-frame-annotation-grid] {
    grid-row: 1 / span 2;
    align-self: center;
    justify-self: center;
  }

  @media (forced-colors: active) {
    &::before {
      background: Highlight;
    }
  }
`;

const ValidationFrameTime = styled.span`
  min-width: 0;
  grid-column: 2;
  grid-row: 1;
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-variant-numeric: tabular-nums;
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`;

const ValidationFrameContext = styled(TruncatedText)`
  width: 100%;
  min-width: 0;
  grid-column: 2;
  grid-row: 2;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
`;

const ValidationFrameLoading = styled.span`
  display: inline-flex;
  grid-row: 1 / span 2;
  align-self: center;
  justify-self: center;
`;

function validationStatusLabel(
  state: AnnotationSetValidationState,
  locale: AnnotationStudioLocale,
  hasUnsavedAnnotationChanges: boolean,
) {
  const saveRequiredForCheck =
    (state.status === "idle" || state.status === "stale") && hasUnsavedAnnotationChanges;
  return annotationStudioText(
    state.status === "validating"
      ? "checkingAnnotations"
      : state.status === "ready"
        ? "ready"
        : state.status === "blocked"
          ? "needsAttention"
          : state.status === "stale"
            ? saveRequiredForCheck
              ? "saveToCheckAnnotations"
              : "annotationSetNotCheckedTitle"
            : state.status === "error"
              ? "annotationSetCheckFailedTitle"
              : saveRequiredForCheck
                ? "saveToCheckAnnotations"
                : "annotationSetNotCheckedTitle",
    locale,
  );
}

function validationWarningState(
  state: AnnotationSetValidationState,
  hasUnsavedAnnotationChanges: boolean,
) {
  const saveRequiredForCheck =
    (state.status === "idle" || state.status === "stale") && hasUnsavedAnnotationChanges;
  return (
    state.status === "blocked" ||
    (state.status === "stale" && saveRequiredForCheck) ||
    state.status === "error"
  );
}

function ValidationStatusIcon({
  state,
  locale,
}: {
  state: AnnotationSetValidationState;
  locale: AnnotationStudioLocale;
}) {
  return state.status === "validating" ? (
    <Spinner label={annotationStudioText("checkingAnnotations", locale)} size="sm" />
  ) : state.status === "ready" ? (
    <CheckCircleIcon />
  ) : state.status === "blocked" ? (
    <WarningIcon />
  ) : state.status === "error" ? (
    <CloseCircleIcon />
  ) : (
    <SearchIcon />
  );
}

export function AnnotationValidationReviewContent({
  state,
  locale,
  targets,
  onOpenIssue,
  onOpenSummary,
  onNavigate,
  summaryLocation,
  issueLocations,
  currentFrameId,
  materializingFrameId = null,
  hasUnsavedAnnotationChanges,
  hasAnnotationContent,
}: {
  state: AnnotationSetValidationState;
  locale: AnnotationStudioLocale;
  targets: readonly AnnotationTarget[];
  onOpenIssue: (issue: AnnotationSetValidationIssue, frameId: string) => boolean | Promise<boolean>;
  onOpenSummary: (() => void) | null;
  onNavigate: () => void;
  summaryLocation: string | null;
  issueLocations: ReadonlyMap<string, AnnotationValidationIssueLocation>;
  currentFrameId: string | null;
  materializingFrameId?: string | null;
  hasUnsavedAnnotationChanges: boolean;
  hasAnnotationContent: boolean;
}) {
  const saveRequiredForCheck =
    (state.status === "idle" || state.status === "stale") && hasUnsavedAnnotationChanges;
  const statusLabel = validationStatusLabel(state, locale, hasUnsavedAnnotationChanges);
  const issueCountLabel = `${state.issues.length}${locale === "ko" ? "" : " "}${annotationStudioText(
    state.issues.length === 1 ? "annotationIssue" : "annotationIssues",
    locale,
  )}`;
  const hasConcreteIssues = state.issues.length > 0;
  const description =
    state.availability.reason ||
    annotationStudioText(
      state.status === "ready"
        ? "annotationSetReady"
        : state.status === "blocked"
          ? "annotationSetBlocked"
          : state.status === "stale"
            ? saveRequiredForCheck
              ? "annotationSetStale"
              : "annotationSetCheckOutdated"
            : state.status === "error"
              ? "annotationSetCheckFailed"
              : state.status === "validating"
                ? "checkingAnnotations"
                : saveRequiredForCheck
                  ? "annotationSetNotChecked"
                  : hasAnnotationContent
                    ? "annotationSetAwaitingCheck"
                    : "annotationSetNoAnnotationChanges",
      locale,
    );

  return (
    <>
      {hasConcreteIssues ? (
        <ValidationIssueCount
          data-annotation-set-validation-issue-count=""
          role="status"
          aria-live="polite"
          variant={defaultTextVariants["caption-bold"]}
        >
          {issueCountLabel}
        </ValidationIssueCount>
      ) : onOpenSummary ? (
        <ValidationSummaryButton
          $status={state.status}
          data-annotation-set-validation-summary-action=""
          aria-live="polite"
          onClick={() => {
            onOpenSummary();
            onNavigate();
          }}
        >
          <ValidationStatusIcon state={state} locale={locale} />
          <ValidationSummaryText>
            <Text variant={defaultTextVariants["caption-bold"]}>{statusLabel}</Text>
            <ValidationDescription variant={defaultTextVariants["caption-normal"]}>
              {description}
              {state.status === "blocked" ? ` · ${issueCountLabel}` : ""}
            </ValidationDescription>
            {summaryLocation ? (
              <ValidationLocation fullText={summaryLocation}>{summaryLocation}</ValidationLocation>
            ) : null}
          </ValidationSummaryText>
        </ValidationSummaryButton>
      ) : (
        <ValidationSummary $status={state.status} role="status" aria-live="polite">
          <ValidationStatusIcon state={state} locale={locale} />
          <ValidationSummaryText>
            <Text variant={defaultTextVariants["caption-bold"]}>{statusLabel}</Text>
            <ValidationDescription variant={defaultTextVariants["caption-normal"]}>
              {description}
              {state.status === "blocked" ? ` · ${issueCountLabel}` : ""}
            </ValidationDescription>
          </ValidationSummaryText>
        </ValidationSummary>
      )}
      {hasConcreteIssues ? (
        <ValidationIssueList>
          {state.issues.map((issue) => {
            const relatedFrames = issueLocations.get(issue.id)?.frames ?? [];
            const relatedFramesLabel = `${annotationStudioText(
              "relatedFrames",
              locale,
            )} · ${relatedFrames.length}`;
            return (
              <li key={issue.id}>
                <ValidationIssueCard
                  data-annotation-set-validation-issue=""
                  aria-label={issue.message[locale]}
                >
                  <ValidationIssueHeader>
                    <WarningIcon aria-hidden="true" />
                    <ValidationIssueText>
                      <span>{issue.message[locale]}</span>
                    </ValidationIssueText>
                  </ValidationIssueHeader>
                  {relatedFrames.length > 0 ? (
                    <ValidationRelatedFrameSection>
                      <ValidationRelatedFrameLabel>
                        {relatedFramesLabel}
                      </ValidationRelatedFrameLabel>
                      <ValidationRelatedFrameList
                        data-validation-related-frame-list={issue.id}
                        aria-label={`${relatedFramesLabel}: ${issue.message[locale]}`}
                      >
                        {relatedFrames.map((relatedFrame) => {
                          const current = relatedFrame.frameId === currentFrameId;
                          const materializing = relatedFrame.frameId === materializingFrameId;
                          return (
                            <li key={relatedFrame.frameId}>
                              <ValidationRelatedFrameButton
                                type="button"
                                $current={current}
                                data-validation-related-frame={relatedFrame.frameId}
                                data-validation-related-frame-current={current ? "" : undefined}
                                aria-current={current ? "true" : undefined}
                                aria-busy={materializing || undefined}
                                disabled={materializing}
                                aria-label={`${annotationStudioText(
                                  "goToCapturedFrame",
                                  locale,
                                )}: ${relatedFrame.path}`}
                                onClick={() => {
                                  void Promise.resolve(
                                    onOpenIssue(issue, relatedFrame.frameId),
                                  ).then((opened) => {
                                    if (opened) onNavigate();
                                  });
                                }}
                              >
                                {materializing ? (
                                  <ValidationFrameLoading data-validation-related-frame-loading="">
                                    <Spinner
                                      label={annotationStudioText("loadingCapturedFrame", locale)}
                                      size="sm"
                                    />
                                  </ValidationFrameLoading>
                                ) : (
                                  <FrameAnnotationGrid
                                    targets={targets}
                                    annotatedTargetIds={relatedFrame.annotatedTargetIds}
                                    data-validation-related-frame-annotation-grid=""
                                  />
                                )}
                                <ValidationFrameTime>
                                  {relatedFrame.timestampLabel}
                                </ValidationFrameTime>
                                <ValidationFrameContext fullText={relatedFrame.contextLabel}>
                                  {relatedFrame.contextLabel}
                                </ValidationFrameContext>
                              </ValidationRelatedFrameButton>
                            </li>
                          );
                        })}
                      </ValidationRelatedFrameList>
                    </ValidationRelatedFrameSection>
                  ) : null}
                </ValidationIssueCard>
              </li>
            );
          })}
        </ValidationIssueList>
      ) : null}
    </>
  );
}

export function AnnotationSetValidationAction({
  state,
  locale,
  targets,
  onValidate,
  onOpenIssue,
  onOpenSummary,
  summaryLocation,
  issueLocations,
  currentFrameId,
  materializingFrameId = null,
  openRequestId = 0,
  hasUnsavedAnnotationChanges,
  hasAnnotationContent,
}: {
  state: AnnotationSetValidationState;
  locale: AnnotationStudioLocale;
  targets: readonly AnnotationTarget[];
  onValidate: () => void;
  onOpenIssue: (issue: AnnotationSetValidationIssue, frameId: string) => boolean | Promise<boolean>;
  onOpenSummary: (() => void) | null;
  summaryLocation: string | null;
  issueLocations: ReadonlyMap<string, AnnotationValidationIssueLocation>;
  currentFrameId: string | null;
  materializingFrameId?: string | null;
  openRequestId?: number;
  hasUnsavedAnnotationChanges: boolean;
  hasAnnotationContent: boolean;
}) {
  const popup = useAnchoredPopupAnchor();
  const popupOpen = popup.open;
  const setPopupAnchor = popup.setAnchor;
  const closePopupAnchor = popup.close;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const previousOpenRequestIdRef = useRef(openRequestId);
  const previousStatusRef = useRef(state.status);
  const [readyResultUnread, setReadyResultUnread] = useState(state.status === "ready");
  const validationTooltip = useTooltipController({ suppressed: popupOpen });

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    if (state.status === "ready" && previousStatus !== "ready") {
      setReadyResultUnread(true);
    } else if (state.status !== "ready") {
      setReadyResultUnread(false);
    }
    previousStatusRef.current = state.status;
  }, [state.status]);

  useEffect(() => {
    const previousOpenRequestId = previousOpenRequestIdRef.current;
    previousOpenRequestIdRef.current = openRequestId;
    if (openRequestId === previousOpenRequestId || popupOpen) return;

    const trigger = triggerRef.current;
    if (!trigger) return;
    setPopupAnchor({
      type: "rect",
      rect: trigger.getBoundingClientRect(),
      el: trigger,
    });
  }, [openRequestId, popupOpen, setPopupAnchor]);

  const closePopup = closePopupAnchor;
  const statusLabel = validationStatusLabel(state, locale, hasUnsavedAnnotationChanges);
  const triggerLabel = `${annotationStudioText("annotationSetCheck", locale)}: ${statusLabel}`;
  const warningState = validationWarningState(state, hasUnsavedAnnotationChanges);
  const statusIconKind =
    state.status === "validating"
      ? "checking"
      : state.status === "ready"
        ? "ready"
        : state.status === "blocked"
          ? "conflict"
          : state.status === "error"
            ? "failed"
            : "check";

  return (
    <>
      <NotificationBadge
        visible={readyResultUnread || warningState}
        tone={state.status === "ready" ? "success" : "warning"}
        size="0.4rem"
        offset="0"
      >
        <Tooltip
          content={triggerLabel}
          placement="bottom"
          controller={validationTooltip}
          describeTrigger={false}
        >
          <IconButton
            ref={triggerRef}
            {...validationTooltip.triggerProps}
            data-annotation-set-validation=""
            data-annotation-set-validation-status={state.status}
            data-annotation-set-validation-icon={statusIconKind}
            size="sm"
            variant="ghost"
            border={false}
            tone={warningState ? "warning" : "primary"}
            selected={popup.open}
            aria-label={triggerLabel}
            aria-haspopup="dialog"
            aria-expanded={popup.open}
            onClick={(event) => {
              if (popup.open) {
                closePopup();
                return;
              }
              if (state.status === "ready") setReadyResultUnread(false);
              popup.openFromElement(event);
            }}
          >
            <ValidationStatusIcon state={state} locale={locale} />
          </IconButton>
        </Tooltip>
      </NotificationBadge>
      <AnchoredPopup
        open={popup.open}
        anchor={popup.anchor}
        onClose={closePopup}
        placement="bottom-end"
        width="20rem"
        maxHeight="24rem"
        returnFocus
        aria-label={annotationStudioText("annotationSetCheck", locale)}
      >
        <AnchoredPopup.Header divider={false}>
          <PopupTitle variant={defaultTextVariants["caption-bold"]}>
            {annotationStudioText("annotationSetCheck", locale)}
          </PopupTitle>
        </AnchoredPopup.Header>
        <AnchoredPopup.Body padding="md" scroll>
          <AnnotationValidationReviewContent
            state={state}
            locale={locale}
            targets={targets}
            onOpenIssue={onOpenIssue}
            onOpenSummary={onOpenSummary}
            onNavigate={closePopup}
            summaryLocation={summaryLocation}
            issueLocations={issueLocations}
            currentFrameId={currentFrameId}
            materializingFrameId={materializingFrameId}
            hasUnsavedAnnotationChanges={hasUnsavedAnnotationChanges}
            hasAnnotationContent={hasAnnotationContent}
          />
        </AnchoredPopup.Body>
        {state.status === "error" ? (
          <AnchoredPopup.Footer align="end">
            <IconButton
              size="sm"
              variant="solid"
              text={annotationStudioText("checkAgain", locale)}
              disabled={!state.availability.allowed}
              onClick={onValidate}
            >
              <CheckCircleIcon />
            </IconButton>
          </AnchoredPopup.Footer>
        ) : null}
      </AnchoredPopup>
    </>
  );
}
